import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DPDPointSelector from "./DPDPointSelector";
import { useTranslation } from "react-i18next";
import Geocode from "react-geocode";
import {
    Coord,
    deliveryOptionsInterface,
    DeliveryType,
    DPDPoint,
    gunOptionsInterface,
    StepProps
} from "../../types/types";
import BookingService from "../../api/BookingService";
import DatePicker from "react-datepicker";
import { addDays, format, isAfter, nextWednesday } from "date-fns";
import {
    beneluxCountries,
    deliveryPriceCalculator,
    getBookingConfig,
    GOOGLE_MAP_API_KEY,
    logError,
    showDate
} from "../../data/price";
import LoadingIndicator from "../utils/LoadingIndicator";
import RentalPointDetails from "../business/RentalPointDetails";
import { DeliveryManager } from "../../data/DeliveryManager";
import { changeDeliveryOptions, setDPDAddress, setDeliveryType, setReturnDate } from '../../stateReducers/deliverySlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DPDDeliveryService } from '../../api/DPDDeliveryService';
import DPDAddressField from '../business/ValidatedAddressField';
import ValidatedAddressField from '../business/ValidatedAddressField';
import { deliveryCodeToReadableName } from '../utils/stateHelper';


const DPDPointPickupReturn: FC<StepProps> = ({setStepValidity}) => {
    const d = useAppDispatch();
    const formType = useAppSelector((state) => {
        return state.delivery.formType;
    });
    const deliveryOptions = useAppSelector((state) => {
        return state.delivery;
    });
    const gunOptions = useAppSelector((state) => {
        return state.gunOptions;
    });
    const dpdSearchAddress = useAppSelector((state) => {
        return state.delivery.dpdSearchAddress;
    });

    useEffect(() => {
        d(setReturnDate(deliveryOptions.maxReturnDate!));
    }, [deliveryOptions.maxReturnDate]);

    const {t} = useTranslation();

    const [poinstAreLoading, setPointsLoading] = useState(false);
    const [dpdPoints, setDpdPoints] = useState<DPDPoint[]>([]);
    const [center, setCenter] = useState<Coord>({lat: 50.9061309, lng: 4.6700651})
    const [needInsurance, setNeedInsurance] = useState(!!deliveryOptions.insuranceAddress);
    const bookingConfig = getBookingConfig();
    let manager = new DeliveryManager(bookingConfig);

    const [dpdPointSpecified, setDPDPointSpecified] = useState<boolean>(false);

    useEffect(() => {
        setDPDPointSpecified(deliveryOptions.dpdPoint !== null);
        let isValid = true;
        // If there's an error with DPD, allow the customer to continue if they've provided a valid
        // address. Then, it'll be automatically selected for them later.
        if (dpdPointsError && !addressValid) {
            isValid = false;
        }
        if (!dpdPointsError && !dpdPointSpecified) {
            isValid = false;
        }
        if (dpdPointSpecified && deliveryOptions.returnDate === undefined) {
            isValid = false;
        }
        if (needInsurance) {
            isValid = isValid && deliveryOptions.insuranceAddress !== undefined;
        }
        setStepValidity(isValid);
        console.log('useEffect with validity: ' + isValid);
    });

    let doGeocoding = useCallback(() => {
        console.log('doGeocoding executed');
        logError('doGeocoding ' + dpdSearchAddress, deliveryOptions);
        setPointsLoading(true);
        Geocode.setApiKey(GOOGLE_MAP_API_KEY);
        Geocode.enableDebug();
        // Get latitude & longitude from address.
        Geocode.fromAddress(dpdSearchAddress || "").then(
            (response: any) => {
                const {lat, lng} = response.results[0].geometry.location;
                setCenter(response.results[0].geometry.location);
                console.log(lat, lng);
                BookingService.getDPDPoints(lat, lng, deliveryOptions.playStart || new Date()).then((response) => {
                    if (response.data.length === 0) {
                        setDPDPointsError(true);
                    } else {
                        setDPDPointsError(false);
                    }
                    setDpdPoints(response.data);
                    setPointsLoading(false);
                    attemptedToLoadPoints.current = true;
                });
            },
            (error: any) => {
                setPointsLoading(false);
                console.error(error);
            }
        );
    }, [dpdSearchAddress, deliveryOptions.playStart]);

    const attemptedToLoadPoints = useRef(false);

    const [dpdPointsError, setDPDPointsError] = useState<boolean>(false);

    const processSearchClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        doGeocoding();
    }
    const isWorkingDate = (date: Date) => {
        if (!deliveryOptions.dpdPoint) {
            return true;
        }
        const weekDay = format(date, 'EEEE')
        let works = deliveryOptions.dpdPoint.schedule.some((day) => {
            //don't allow return date on weekend
            if (day.weekday !== weekDay || day.weekday === 'Saturday' || day.weekday === 'Sunday') {
                return false;
            } else {
                return day.openMorning !== '00:00' || day.openAfternoon !== '00:00';
            }
        });
        // console.log('working date', format(date,"yyyy-MM-dd"),works);
        return works;
    };
    const calculateMinReturnDate = useCallback(() => {
        if (!deliveryOptions.playEnd) {
            return new Date();
        }

        console.log(showDate(DPDDeliveryService.earliestReturnDateAfter(deliveryOptions.playEnd)));

        return DPDDeliveryService.earliestReturnDateAfter(deliveryOptions.playEnd);
    }, [deliveryOptions.playEnd]);

    const calculateMaxReturnDate = useCallback(() => {
        if (formType === 'YouthPromo') {
            return calculateMinReturnDate();
        } else {
            return nextWednesday(deliveryOptions.playEnd ?? new Date());
        }
    }, [deliveryOptions.playEnd]);

    const processInsuranceClick = (checked: boolean) => {
        logError('processInsuranceClick ' + checked, deliveryOptions);
        setNeedInsurance(checked);
        if (!checked) {
            d(changeDeliveryOptions({insuranceAddress: undefined}));
        }
    }
    const processPointClick = (point: DPDPoint) => {
        setCenter({lat: point.lat, lng: point.lng});
        console.log('point clicked', JSON.stringify(point));
        logError('processPointClick ', point);
        if (beneluxCountries.includes(bookingConfig.country ?? 'be')) {
            if (
                (
                    manager.isAmountEnoughForDeliveryInRegion(gunOptions.quantity || 0)
                    && manager.isFreeZip(point.zip)
                ) ||
                manager.isFreeZIPBasedOnUrl(point.zip)
                ||
                manager.isFreePhoenixDelivery(gunOptions.quantity || 0)
                ||
                (deliveryOptions.playStart && manager.isFreeDeliveryDate(deliveryOptions.playStart))
            ) {
                d(setDeliveryType(DeliveryType.Free));
                d(changeDeliveryOptions({dpdPoint: point}));
            } else {
                d(setDeliveryType(DeliveryType.DPDPoint));
                d(changeDeliveryOptions({dpdPoint: point}));
            }
        } else {
            if (deliveryOptions.deliveryType === DeliveryType.ProbablyFree) {
                // ProbablyFree means that there wasn't a different promotion, and ProbablyFree doesn't work outside
                // the Benelux
                d(setDeliveryType(DeliveryType.DPDPoint));
            }
            // Outside Benelux, so only standard promo. deliveryType was decided during step2, so just change the point
            d(changeDeliveryOptions({dpdPoint: point}));
        }
    }
    const onReturnDateSelect = (date: Date | null) => {
        logError('onReturnDateSelect ' + format(date || new Date(), "yyyy-MM-dd"), deliveryOptions);
        if (date === null) {
            return;
        }
        d(setReturnDate(date));
    }

    const [addressValid, setAddressValid] = useState<boolean>(false);

    return (
        <div>
            <div className="block bg-white max-w-full">
                <div className="form-group mb-2 flex flex-row">
                    <ValidatedAddressField
                        label={t('delivery.homeAddressLabel')}
                        address={useAppSelector((state) => state.delivery.dpdSearchAddress)}
                        setAddress={(address: string | undefined) => { d(setDPDAddress(address)); }}
                        addressValid={addressValid}
                        setAddressValid={setAddressValid}
                        mustBeBelgianAddress={false}
                        mustHaveStreetNumber={false}
                        wallonianAddressesAllowed={true}
                    />
                </div>
                {!poinstAreLoading && <div>
                    <button
                        className={
                            `cursor-pointer inline-block mb-2 px-6 py-2.5 
                            text-white font-medium text-xs leading-tight uppercase rounded shadow-md 
                            hover:shadow-lg focus:shadow-lg focus:outline-none focus:ring-0 
                            active:shadow-lg 
                            ${!addressValid ?
                                "bg-gray-300 disabled:bg-gray-100 focus:bg-gray-400 active:bg-gray-400"
                                :
                                "bg-orange-300 disabled:bg-orange-100 hover:bg-orange-400 focus:bg-orange-400 active:bg-orange-400"}`
                        }
                        onClick={(e) => processSearchClick(e)}>
                        {t('delivery.dpdSearchButtonText')}
                    </button>
                </div>
                }
                {poinstAreLoading && <LoadingIndicator text={t('delivery.dpdPointsLoadingText')}/>}
            </div>
            {dpdPoints.length > 0 &&
                <DPDPointSelector center={center} chosenPoint={deliveryOptions.dpdPoint} points={dpdPoints}
                                  onPointSelect={(point) => processPointClick(point)}
                                  formType={formType}/>}
            {deliveryOptions.dpdPoint &&
                <div className="xl:w-1/2">
                    <p><b>{t('delivery.finalPrice')}: {
                        deliveryOptions.deliveryType === 'free'
                        ?
                            t('delivery.free')
                            :
                            t('delivery.' + deliveryOptions.deliveryType, {
                                "price": deliveryPriceCalculator(gunOptions, deliveryCodeToReadableName(deliveryOptions.deliveryType!) ?? "")?.price?.toString() ?? "?"
                            }
                            )
                    }</b></p>
                    <p><b>{t('delivery.selectedDPDParcelShop')}:</b></p>
                    <RentalPointDetails point={deliveryOptions.dpdPoint} selected={true} hovered={false}/>
                </div>
            }
            {deliveryOptions.dpdPoint && deliveryOptions.dpdPoint.id && <div className="form-group mb-6 flex flex-row">
                <div className="mb-3 w-full">
                    <label htmlFor="returnDate"
                           className="form-label inline-block mb-2 text-gray-700">{t('delivery.selectReturnDateLabel')}
                        <span
                            className="text-red-500 font-bold text-2xl">*</span> </label>
                    <DatePicker
                        className="form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-orange-200 focus:shadow-none focus:outline-none focus:outline-none"
                        onChange={onReturnDateSelect}
                        selected={deliveryOptions.returnDate}
                        filterDate={isWorkingDate}
                        id="returnDate"
                        placeholderText={t('delivery.selectReturnDatePlaceholder')}
                        minDate={calculateMinReturnDate()}
                        maxDate={calculateMaxReturnDate()}
                        calendarStartDay={1}
                        withPortal
                        portalId="modal-root"
                        dateFormat="dd-MM-yyyy"
                    />
                </div>
            </div>
            }
            {dpdPointsError && <div className='form-label inline-block text-gray-700'>
                <p style={{color: 'red'}}>
                    {t('delivery.dpdPoint.error')}
                </p>
                <p>
                    {t('delivery.dpdPoint.returnBy')}
                </p>
                <p>
                    {format(deliveryOptions.maxReturnDate!,"yyy-MM-dd")}
                </p>
            </div>
            }
            <div className="form-group mb-6 flex flex-row">
                <div className="mb-3 w-full">
                    <div className="form-check">
                        <input
                            className="form-check-input  h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300"
                            type="checkbox" checked={needInsurance}
                            onChange={(e) => processInsuranceClick(e.target.checked)} value=""
                            id="insuranceCheckbox"/>
                        <label className="form-check-label inline-block text-gray-800"
                                htmlFor="insuranceCheckbox">
                            {t('delivery.insuranceAddressCheckbox')}
                        </label>
                    </div>
                </div>
            </div>
            {needInsurance &&
                <div className="form-group mb-6 flex flex-row">
                    <div className="mb-3 w-full">
                        <label htmlFor="insuranceAddress"
                               className="form-label inline-block mb-2 text-gray-700">{t('delivery.insuranceAddressLabel')}</label>
                        <input
                            type="text"
                            className="
                                form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding
                                border border-solid border-gray-300
                                rounded
                                m-0
                                focus:text-gray-700 focus:bg-white focus:border-amber-400 focus:outline-none
                              "
                            value={deliveryOptions.insuranceAddress}
                            onChange={(e) => d(changeDeliveryOptions({insuranceAddress: e.target.value}))}
                            id="insuranceAddress"
                            placeholder={t('delivery.insuranceAddress')}
                        />
                    </div>
                </div>
            }

        </div>
    );
};

export default DPDPointPickupReturn;
